"use client";

import { useRouter } from "next/navigation";
import { useIsDesktop } from "@/hooks/useIsDesktop";

const PJK = "'Plus Jakarta Sans', var(--font-plus-jakarta), sans-serif";

interface Review {
  stars: number;
  timeAgo: string;
  text: string;
}

interface AgentProfileData {
  name: string;
  initials: string;
  avatarBg: string;
  status: string;
  rating: number;
  tasksCompleted: number;
  description: string;
  specialties: string[];
  pricingRange: string;
  stats: { success: string; avgSpeed: string; disputes: string };
  reviews: Review[];
}

export default function AgentProfilePanel({
  agent,
  onClose,
  onHire,
}: {
  agent: AgentProfileData;
  onClose?: () => void;
  onHire?: () => void;
}) {
  const router = useRouter();
  const handleClose = () => {
    if (onClose) onClose();
    else router.back();
  };

  // 5-star display: floor(rating) filled, rest outline
  const filledStars = Math.floor(agent.rating);
  const isDesktop = useIsDesktop();
  const avatarSize = isDesktop ? 52 : 64;
  const avatarRadius = isDesktop ? 13 : 16;
  const avatarFontSize = isDesktop ? 20 : 24;

  return (
    <div
      className="desktop-glass-panel"
      style={{
        width: "100%",
        height: "100%",
        background: "#171717",
        borderLeft: "1px solid rgba(242,240,237,0.08)",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
      }}
    >
      {/* Header */}
      <div
        className="desktop-glass-profile-section"
        style={{
          width: "100%",
          padding: 24,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
          <div
            style={{
              width: avatarSize,
              height: avatarSize,
              borderRadius: avatarRadius,
              background: agent.avatarBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow:
                "0px 10px 15px -3px rgba(0,0,0,0.1), 0px 4px 6px -4px rgba(0,0,0,0.1)",
              flexShrink: 0,
            }}
          >
            <span
              style={{
                fontFamily: PJK,
                fontWeight: 800,
                fontSize: avatarFontSize,
                lineHeight: `${Math.round(avatarFontSize * 1.4)}px`,
                color: "white",
                textAlign: "center",
              }}
            >
              {agent.initials}
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span
                style={{
                  fontFamily: PJK,
                  fontWeight: 700,
                  fontSize: 20,
                  lineHeight: "28px",
                  color: "#F2F0ED",
                }}
              >
                {agent.name}
              </span>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 9999,
                  background: "#4ADE80",
                  display: "inline-block",
                }}
              />
            </div>
            <span
              style={{
                fontFamily: PJK,
                fontWeight: 600,
                fontSize: 12,
                lineHeight: "16px",
                letterSpacing: "0.3px",
                color: "#4ADE80",
                textTransform: "uppercase",
              }}
            >
              {agent.status}
            </span>
          </div>
        </div>

        {/* Close button */}
        <button
          onClick={handleClose}
          aria-label="Close"
          className="app-close-btn"
          style={{
            width: 42.8,
            height: 42.8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: 0,
          }}
        >
          <span style={{ display: "flex", transform: "scale(0.95)" }}>
            <span
              style={{
                display: "flex",
                alignItems: "center",
                padding: 12,
                borderRadius: 9999,
              }}
            >
              <img
                src="/app-icons/multiply.svg"
                alt=""
                style={{ width: 20, height: 20, color: "#848382" }}
              />
            </span>
          </span>
        </button>
      </div>

      {/* Scrollable content — flex:1 with minHeight:0 is the pattern that
          lets a nested column scroll instead of growing its parent. */}
      <div
        className="app-scrollbar desktop-glass-profile-section"
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          width: "100%",
          overflowY: "auto",
          padding: "0 24px 24px",
          display: "flex",
          flexDirection: "column",
          gap: 23.3,
          alignItems: "flex-start",
        }}
      >
        {/* Rating row */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
          <div style={{ display: "flex", gap: 2, alignItems: "center" }}>
            {[0, 1, 2, 3, 4].map((i) => (
              <img
                key={i}
                src={
                  i < filledStars
                    ? "/app-icons/star-mini.svg"
                    : "/app-icons/star-mini-outline.svg"
                }
                alt=""
                style={{ width: 11.667, height: 11.083 }}
              />
            ))}
          </div>
          <span
            style={{
              fontFamily: PJK,
              fontWeight: 700,
              fontSize: 16,
              lineHeight: "24px",
              color: "#F2F0ED",
            }}
          >
            {agent.rating.toFixed(1)}
          </span>
          <span
            style={{
              fontFamily: PJK,
              fontWeight: 400,
              fontSize: 14,
              lineHeight: "20px",
              color: "rgba(242,240,237,0.5)",
            }}
          >
            ({agent.tasksCompleted.toLocaleString()} tasks completed)
          </span>
        </div>

        {/* Description */}
        <p
          style={{
            fontFamily: PJK,
            fontWeight: 400,
            fontSize: 15,
            lineHeight: "24.38px",
            color: "rgba(242,240,237,0.9)",
            margin: 0,
            width: "100%",
          }}
        >
          {agent.description}
        </p>

        {/* Specialties */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            paddingTop: 8.7,
            paddingBottom: 8.7,
            alignItems: "flex-start",
            width: "100%",
          }}
        >
          <SectionLabel>SPECIALTIES</SectionLabel>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start", width: "100%" }}>
            {agent.specialties.map((s) => (
              <div
                key={s}
                className="app-specialty-pill"
                style={{
                  border: "1px solid #86BBD8",
                  borderRadius: 9999,
                  padding: "7px 13px",
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <span
                  style={{
                    fontFamily: PJK,
                    fontWeight: 600,
                    fontSize: 12,
                    lineHeight: "16px",
                    color: "#86BBD8",
                  }}
                >
                  {s}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Pricing */}
        <div
          style={{
            background: "#232323",
            borderRadius: 16,
            padding: 16,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 4,
            alignItems: "flex-start",
          }}
        >
          <span
            style={{
              fontFamily: PJK,
              fontWeight: 700,
              fontSize: 10,
              lineHeight: "15px",
              letterSpacing: "1px",
              textTransform: "uppercase",
              color: "#F6AE2D",
            }}
          >
            PRICING
          </span>
          <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
            <span
              style={{
                fontFamily: PJK,
                fontWeight: 800,
                fontSize: 24,
                lineHeight: "32px",
                letterSpacing: "-0.6px",
                color: "#F6AE2D",
              }}
            >
              {agent.pricingRange}
            </span>
            <span
              style={{
                fontFamily: PJK,
                fontWeight: 500,
                fontSize: 12,
                lineHeight: "16px",
                color: "rgba(246,174,45,0.7)",
              }}
            >
              per task
            </span>
          </div>
        </div>

        {/* Stats Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: 12,
            width: "100%",
          }}
        >
          <StatBox label="SUCCESS" value={agent.stats.success} />
          <StatBox label="AVG SPEED" value={agent.stats.avgSpeed} />
          <StatBox label="DISPUTES" value={agent.stats.disputes} />
        </div>

        {/* Recent Reviews */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            paddingTop: 16.7,
            width: "100%",
            alignItems: "flex-start",
          }}
        >
          <SectionLabel>RECENT REVIEWS</SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%" }}>
            {agent.reviews.map((r, i) => (
              <div
                key={i}
                className="app-review-card"
                style={{
                  background: "rgba(255,255,255,0.02)",
                  borderTopLeftRadius: 24,
                  borderTopRightRadius: 24,
                  borderBottomRightRadius: 24,
                  borderBottomLeftRadius: 0,
                  // Figma 88:8104: px-16 py-12 on desktop; web keeps 16/16.
                  padding: isDesktop ? "12px 16px" : 16,
                  display: "flex",
                  flexDirection: "column",
                  gap: 7.125,
                  alignItems: "flex-start",
                  width: "100%",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <div style={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                    {Array.from({ length: 5 }).map((_, si) => (
                      <img
                        key={si}
                        src="/app-icons/star-mini.svg"
                        alt=""
                        style={{ width: 10, height: 9.5 }}
                      />
                    ))}
                  </div>
                  <span
                    style={{
                      fontFamily: PJK,
                      fontWeight: 400,
                      fontSize: 10,
                      lineHeight: "15px",
                      color: "rgba(242,240,237,0.5)",
                    }}
                  >
                    {r.timeAgo}
                  </span>
                </div>
                <p
                  style={{
                    fontFamily: PJK,
                    fontWeight: 400,
                    fontSize: 14,
                    lineHeight: "19.25px",
                    color: "rgba(242,240,237,0.8)",
                    margin: 0,
                    width: "100%",
                  }}
                >
                  &ldquo;{r.text}&rdquo;
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Sticky footer — in-flow, pinned to the bottom by the flex column.
          Shadow above it separates from scrolling content. */}
      <div
        className="desktop-glass-profile-footer"
        style={{
          flexShrink: 0,
          width: "100%",
          background: "#1E1E1E",
          borderTop: "1px solid rgba(242,240,237,0.08)",
          boxShadow: "0px -10px 30px 0px rgba(0,0,0,0.5)",
          paddingTop: 25,
          paddingBottom: 24,
          paddingLeft: 24,
          paddingRight: 24,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <button
          onClick={onHire}
          className="app-btn-primary"
          style={{
            background: "#8635F6",
            borderRadius: 9999,
            width: "100%",
            // Figma 88:8104 specifies h-[40px] on desktop; the web
            // button keeps 16/16 vertical padding (~56px pill).
            paddingTop: isDesktop ? 8 : 16,
            paddingBottom: isDesktop ? 8 : 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            border: "none",
            cursor: "pointer",
          }}
        >
          <span
            style={{
              fontFamily: PJK,
              fontWeight: 700,
              fontSize: isDesktop ? 14 : 16,
              lineHeight: "24px",
              color: "#F2F0ED",
              textAlign: "center",
            }}
          >
            Hire this agent
          </span>
          <img
            src="/app-icons/arrow-right.svg"
            alt=""
            style={{ width: 24, height: 24 }}
          />
        </button>
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        fontFamily: PJK,
        fontWeight: 700,
        fontSize: 10,
        lineHeight: "15px",
        letterSpacing: "1px",
        textTransform: "uppercase",
        color: "rgba(242,240,237,0.5)",
      }}
    >
      {children}
    </span>
  );
}

function StatBox({ label, value }: { label: string; value: string }) {
  return (
    <div
      className="app-stat-box"
      style={{
        background: "rgba(255,255,255,0.05)",
        borderRadius: 16,
        padding: 12,
        display: "flex",
        flexDirection: "column",
        gap: 4,
        alignItems: "center",
        cursor: "default",
      }}
    >
      <span
        style={{
          fontFamily: PJK,
          fontWeight: 700,
          fontSize: 10,
          lineHeight: "15px",
          letterSpacing: "1px",
          textTransform: "uppercase",
          color: "rgba(242,240,237,0.5)",
          textAlign: "center",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: PJK,
          fontWeight: 700,
          fontSize: 18,
          lineHeight: "28px",
          color: "#F2F0ED",
          textAlign: "center",
        }}
      >
        {value}
      </span>
    </div>
  );
}
